import { IconNames } from '@sendible/design-system';
import { useTranslation } from 'react-i18next';
import { COMPOSE_TABS } from '../components/MediaSystem/consts';
import { ALT_TEXT_SUPPORTED_TABS, VIDEO_SETTINGS_SUPPORTED_TABS } from '../constants/feature-support';
import { useComposeBoxContext } from '../components/MediaSystem/composeBoxContext';
import { getThumbnailSupportedProfilesFromSelection } from '../utils/videoThumbnailSupport/getThumbnailSupportedProfilesFromSelection';

interface MediaControl {
  action: string;
  badge?: string;
  icon: IconNames;
  isHighlighted?: boolean;
  label: string;
  altLabel?: string;
}

// NOTE: all currently selected network types, whether from individually-selected
// profiles (each with their own tab) or from a selected profile list (reflected in
// profileListServices) - used to detect an "only LinkedIn selected" message tab.
const getCanEditTitle = (tabId: composeTabs, activeNetworkTabs: composeTabs[], profileListServices: composeTabs[]): boolean => {
  const allSelectedNetworkTypes = [
    ...(Array.isArray(activeNetworkTabs) ? activeNetworkTabs : []),
    ...(Array.isArray(profileListServices) ? profileListServices : []),
  ];
  const onlyLinkedInSelected = allSelectedNetworkTypes.length > 0 && allSelectedNetworkTypes.every((tab) => tab === COMPOSE_TABS.LINKEDIN);

  return tabId === COMPOSE_TABS.GROUP
    ? (Array.isArray(profileListServices) ? profileListServices : []).includes(COMPOSE_TABS.LINKEDIN)
    : tabId === COMPOSE_TABS.LINKEDIN || (tabId === COMPOSE_TABS.MESSAGE && onlyLinkedInSelected);
};

export const useMediaControls = (tabId: composeTabs = COMPOSE_TABS.MESSAGE) => {
  const { t } = useTranslation('compose_box');
  const { activePostType, profileListServices, activeNetworkTabs } = useComposeBoxContext();
  const editImage: MediaControl = {
    action: 'edit',
    label: t('mediaCardDropdownOptions_edit'),
    icon: 'brush',
  };
  const editVideo: MediaControl = {
    action: 'edit',
    label: t('mediaCardDropdownOptions_edit'),
    icon: 'brush',
    badge: 'NEW',
  };

  const remove: MediaControl = {
    action: 'remove',
    label: t('mediaCardDropdownOptions_remove'),
    isHighlighted: true,
    icon: 'minus_circle',
  };
  const download: MediaControl = {
    action: 'download',
    label: t('mediaCardDropdownOptions_download'),
    icon: 'download',
  };

  const altText: MediaControl = {
    action: 'alttext',
    label: t('mediaCardDropdownOptions_altText'),
    icon: 'eye_off',
  };

  const settings: MediaControl = {
    action: 'settings',
    label: t('mediaCardDropdownOptions_settings'),
    icon: 'video',
  };
  const tag: MediaControl = {
    action: 'tag',
    label: t('mediaCardDropdownOptions_tag'),
    icon: 'tag',
  };

  const title: MediaControl = {
    action: 'title',
    label: t('mediaCardDropdownOptions_title'),
    icon: 'text',
  };

  const supportsAltText = ALT_TEXT_SUPPORTED_TABS.includes(tabId);

  const selectedGroupSupportedServices = getThumbnailSupportedProfilesFromSelection(Array.isArray(profileListServices) ? profileListServices : []);
  const supportsVideoSettings =
    tabId === COMPOSE_TABS.GROUP ? selectedGroupSupportedServices.length > 0 : VIDEO_SETTINGS_SUPPORTED_TABS.includes(tabId);

  const supportsTagging = tabId === COMPOSE_TABS.INSTAGRAM && activePostType === 'post';

  const canEditTitle = getCanEditTitle(tabId, activeNetworkTabs, profileListServices);

  const imageDropdownOptions = [editImage, ...(supportsAltText ? [altText] : []), ...(supportsTagging ? [tag] : []), download, remove];

  const gifDropdownOptions = [...(supportsAltText ? [altText] : []), ...(supportsTagging ? [tag] : []), download, remove];

  const videoDropdownOptions = [editVideo, ...(supportsVideoSettings ? [settings] : []), download, remove];
  const pdfDropdownOptions = [...(canEditTitle ? [title] : []), download, remove];

  const dropdownActions: { [key: string]: MediaControl[] } = {
    image: imageDropdownOptions,
    video: videoDropdownOptions,
    document: pdfDropdownOptions,
    gif: gifDropdownOptions,
  };

  return { dropdownActions, canEditTitle };
};
